import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, RefreshCw, Download, Eye, X } from "lucide-react";
import Breadcrumb, {
  BREADCRUMB_ACTION_BTN,
} from "../../../components/Breadcrumb/Breadcrumb";
import Table, { TABLE_THEME } from "../../../components/Table/Table";
import payRollService from "../../../services/PayrollService";
import {
  fetchSalaryComponentSummary,
  fetchPayrollBreakdown,
  clearPayrollBreakdown,
  selectEntity,
  fetchPayrollEntityList,
  findComponentAmount,
  COMPONENT_ALIASES,
} from "../../../store/slices/payrollSlice";
import {
  fetchMonthlyAttendanceSummary,
  selectMonthlySummary,
  selectCompanyWorkingDays,
} from "../../../store/slices/filterAttendanceSlice";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getGradeList } from "../../../store/slices/gradeSlice";

const FIELD = `${TABLE_THEME.fieldBase} ${TABLE_THEME.fieldFocus}`;
const LABEL = TABLE_THEME.fieldLabel;

// ─── Selectors ──────────────────────────────────────────────────────────────
const selectSalaryComponentSummary = (state) =>
  state.payroll?.salaryComponentSummary?.employees || [];
const selectSalarySummaryLoading = (state) =>
  state.payroll?.salaryComponentSummary?.loading || false;
const selectSalarySummaryError = (state) =>
  state.payroll?.salaryComponentSummary?.error || null;

const selectBreakdownData = (state) =>
  state.payroll?.payrollBreakdown?.data || {};
const selectBreakdownLoading = (state) =>
  state.payroll?.payrollBreakdown?.loading || {};
const selectBreakdownErrors = (state) =>
  state.payroll?.payrollBreakdown?.error || {};

/*
|--------------------------------------------------------------------------
| Helper: fetch a real payroll period ID
|--------------------------------------------------------------------------
*/
async function fetchPeriodList() {
  try {
    const response = await payRollService.GetPayRollPeriodList();
    const resultSet = response?.data?.ResultSet || response?.ResultSet || [];
    return resultSet
      .filter((p) => p.HPP_STATUS === "P")
      .map((p) => ({
        id: p.HPP_PAYROLL_PERIOD_ID,
        name: p.HPP_PERIOD_NAME,
      }));
  } catch (err) {
    console.error("Failed to fetch payroll period list:", err);
    return [];
  }
}

/*
|--------------------------------------------------------------------------
| Field labels
|--------------------------------------------------------------------------
*/
const FIELD_LABELS = {
  HED_SERVICE_NO: "Staff Id",
  HED_REPORT_NAME: "Name",
  HLD_LOCATION_NAME: "Location",
  HED_NIC_NO: "NIC",
  HED_DATE_OF_JOINING: "Date of Joining",
  HED_LEAVING_DATE: "Date of Leaving",
  HED_DATE_OF_BIRTH: "Date of Birth",
  HED_GENDER: "Gender",
  HED_DESIGNATION_NAME: "Designation",
  HEC_CATEGORY_NAME: "Employment Category",
  HED_IS_ACTIVE: "Status",
  HED_GROUP: "Group",
  HDD_DEPARTMENT_NAME: "Department",
  HED_COSTCENTER: "Cost Center",
  HPP_PERIOD_NAME: "Month",
  HPH_NET_SALARY: "Net Salary",
};

/*
|--------------------------------------------------------------------------
| Visible columns for the on-screen table
|--------------------------------------------------------------------------
*/
const VISIBLE_COLUMNS = [
  "HED_SERVICE_NO",
  "HED_REPORT_NAME",
  "HLD_LOCATION_NAME",
  "HED_NIC_NO",
  "HED_DATE_OF_JOINING",
  "HED_LEAVING_DATE",
  "HED_DATE_OF_BIRTH",
  "HED_GENDER",
  "HED_DESIGNATION_NAME",
  "HEC_CATEGORY_NAME",
  "HED_IS_ACTIVE",
  "HED_GROUP",
  "HDD_DEPARTMENT_NAME",
  "HED_COSTCENTER",
  "HPP_PERIOD_NAME",
  "HPH_NET_SALARY",
];

/*
|--------------------------------------------------------------------------
| Formatting helpers
|--------------------------------------------------------------------------
*/
const DATE_FIELDS = new Set([
  "HED_DATE_OF_BIRTH",
  "HED_DATE_OF_JOINING",
  "HED_LEAVING_DATE",
]);

const CURRENCY_FIELDS = new Set(["HPH_NET_SALARY"]);

const formatDateValue = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return "-";
  }
};

const formatStatus = (status) => {
  if (status === "True" || status === true) return "Current";
  if (status === "False" || status === false) return "ExEmployee";
  return status || "-";
};

const getStatusColor = (status) => {
  const colorMap = {
    Current: "bg-green-100 text-green-800",
    ExEmployee: "bg-red-100 text-red-800",
    Probation: "bg-yellow-100 text-yellow-800",
  };
  return colorMap[status] || "bg-gray-100 text-gray-800";
};

const formatCellValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";

  if (key === "HED_GENDER") {
    if (value === "M") return "Male";
    if (value === "F") return "Female";
    return value;
  }

  if (DATE_FIELDS.has(key)) return formatDateValue(value);

  if (CURRENCY_FIELDS.has(key)) {
    const num = Number(value);
    if (Number.isNaN(num) || num === 0) return "-";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  if (key === "HED_IS_ACTIVE") return formatStatus(value);

  return String(value);
};

const COMPONENT_TYPE_LABEL = {
  E: "Earning",
  D: "Deduction",
  C: "Contribution",
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const excelColumnLetter = (n) => {
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/* ──────────────────────────────────────────────────────────────────
   Export helpers — used only by exportToExcel
   ────────────────────────────────────────────────────────────────── */

function toDateCell(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  return isNaN(d.getTime()) ? raw : d;
}

function genderLabel(g) {
  if (g === "M") return "Male";
  if (g === "F") return "Female";
  return g ?? "";
}

function asBool(v) {
  if (v === "True" || v === true || v === 1 || v === "1") return 1;
  if (v === "False" || v === false || v === 0 || v === "0") return 0;
  return v ?? "-";
}

function asYesNo(v) {
  if (v === "True" || v === true || v === 1 || v === "1") return "Yes";
  if (v === "False" || v === false || v === 0 || v === "0") return "No";
  return v ?? "-";
}

function companyFromLocation(locationName) {
  if (!locationName) return "-";
  const part = String(locationName).split(" - ")[0].trim();
  return part || "-";
}

const MONTH_NAME_TO_NUM = {
  jan: "01", january: "01",
  feb: "02", february: "02",
  mar: "03", march: "03",
  apr: "04", april: "04",
  may: "05",
  jun: "06", june: "06",
  jul: "07", july: "07",
  aug: "08", august: "08",
  sep: "09", sept: "09", september: "09",
  oct: "10", october: "10",
  nov: "11", november: "11",
  dec: "12", december: "12",
};

function parsePeriodName(periodName) {
  if (!periodName) return null;
  const s = String(periodName).trim();

  // "2026-09" or "2026/09"
  let m = s.match(/^(\d{4})[-/](\d{1,2})$/);
  if (m) {
    return { year: m[1], month: m[2].padStart(2, "0") };
  }

  m = s.match(/^([A-Za-z]+)[\s-]*(\d{4})$/);
  if (m) {
    const key = m[1].toLowerCase();
    const num = MONTH_NAME_TO_NUM[key];
    if (num) return { year: m[2], month: num };
  }

  // "2026 September" / "2026 Sep"
  m = s.match(/^(\d{4})[\s-]*([A-Za-z]+)$/);
  if (m) {
    const key = m[2].toLowerCase();
    const num = MONTH_NAME_TO_NUM[key];
    if (num) return { year: m[1], month: num };
  }

  return null;
}

/* ──────────────────────────────────────────────────────────────────
   Salary Breakdown Modal
   ────────────────────────────────────────────────────────────────── */
function SalaryBreakdownModal({ open, onClose, employee, state, onRetry }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const renderSection = (title, items, total, totalLabel) => (
    <div className="mb-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
        {title}
      </div>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-1 pr-3">Code</th>
            <th className="py-1 pr-3">Description</th>
            <th className="py-1 pr-3 text-right">Qty</th>
            <th className="py-1 pr-3 text-right">%</th>
            <th className="py-1 pr-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={5} className="py-1 text-gray-400 italic">
                No {title.toLowerCase()} for this period.
              </td>
            </tr>
          ) : (
            items.map((r) => (
              <tr key={r.id} className="border-b border-gray-100">
                <td className="py-1 pr-3 font-mono text-xs">
                  {r.componentCode || "-"}
                </td>
                <td className="py-1 pr-3">
                  {r.description || r.componentName || "-"}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {r.quantity != null ? r.quantity.toFixed(2) : "-"}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {r.percentage != null ? r.percentage.toFixed(2) : "-"}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {fmtMoney(r.amount)}
                </td>
              </tr>
            ))
          )}
          <tr className="font-semibold bg-gray-50">
            <td colSpan={4} className="py-1 pr-3 text-right">
              {totalLabel}
            </td>
            <td className="py-1 pr-3 text-right tabular-nums">
              {fmtMoney(total)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const data = state?.data;
  const header = data?.header;
  const totalContributions = data?.contributions
    ? data.contributions.reduce((s, r) => s + r.amount, 0)
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Salary Breakdown
            </h3>
            <p className="text-xs text-gray-500">
              {employee?.HED_REPORT_NAME || "-"} · Staff Id{" "}
              {employee?.HED_SERVICE_NO || "-"}
              {header?.payrollNo ? ` · ${header.payrollNo}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto">
          {state?.loading && (
            <div className="py-10 text-center text-sm text-gray-500 italic">
              Loading salary breakdown...
            </div>
          )}

          {state?.error && !state?.loading && (
            <div className="py-6 text-sm text-red-600 flex items-center justify-center gap-3">
              <span>{state.error}</span>
              <button
                onClick={onRetry}
                className="text-xs rounded border border-red-300 px-2 py-1 hover:bg-red-50"
              >
                Retry
              </button>
            </div>
          )}

          {!state?.loading && !state?.error && !data && (
            <div className="py-10 text-center text-sm text-gray-500 italic">
              No payroll details found for this employee in this period.
            </div>
          )}

          {!state?.loading && !state?.error && data && (
            <>
              {header && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <SummaryTile
                    label="Working Days"
                    value={header.workingDays.toFixed(2)}
                  />
                  <SummaryTile
                    label="Present Days"
                    value={header.presentDays.toFixed(2)}
                  />
                  <SummaryTile
                    label="No-Pay Days"
                    value={header.noPayDays.toFixed(2)}
                  />
                  <SummaryTile
                    label="OT Hours"
                    value={header.otHours.toFixed(2)}
                  />
                </div>
              )}

              {renderSection(
                "Earnings",
                data.earnings,
                data.totalEarnings,
                "Total Earnings"
              )}
              {renderSection(
                "Deductions",
                data.deductions,
                data.totalDeductions,
                "Total Deductions"
              )}
              {data.contributions.length > 0 &&
                renderSection(
                  "Employer Contributions (not deducted)",
                  data.contributions,
                  totalContributions,
                  "Total Contributions"
                )}

              {header && (
                <div className="mb-4 flex flex-wrap gap-3 text-xs text-gray-600">
                  <span className="rounded border border-gray-200 px-2 py-1">
                    Employee EPF: <b>{fmtMoney(header.employeeEpf)}</b>
                  </span>
                  <span className="rounded border border-gray-200 px-2 py-1">
                    Employer EPF: <b>{fmtMoney(header.employerEpf)}</b>
                  </span>
                  <span className="rounded border border-gray-200 px-2 py-1">
                    Employer ETF: <b>{fmtMoney(header.employerEtf)}</b>
                  </span>
                  <span className="rounded border border-gray-200 px-2 py-1">
                    PAYE: <b>{fmtMoney(header.paye)}</b>
                  </span>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <div className="rounded bg-green-50 border border-green-200 px-4 py-2">
                  <span className="text-xs uppercase tracking-wide text-green-700 mr-3">
                    Net Salary
                  </span>
                  <span className="font-semibold text-green-700 tabular-nums">
                    {fmtMoney(data.netSalary)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="rounded border border-gray-300 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="text-sm font-semibold text-gray-800 tabular-nums">
        {value}
      </div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/
function SalarySummary() {
  const dispatch = useDispatch();

  const employees = useSelector(selectSalaryComponentSummary);
  const loading = useSelector(selectSalarySummaryLoading);
  const error = useSelector(selectSalarySummaryError);

  const breakdowns = useSelector(selectBreakdownData);
  const breakdownLoading = useSelector(selectBreakdownLoading);
  const breakdownErrors = useSelector(selectBreakdownErrors);

  const monthlySummary = useSelector(selectMonthlySummary);
  const companyWorkingDays = useSelector(selectCompanyWorkingDays);

  const { list: allComponents } = useSelector(
    selectEntity("payrollComponents")
  );

  const grades = useSelector((state) => state.gradeSlice?.grades || []);
  const [selectedGrade, setSelectedGrade] = useState("");

  const [search, setSearch] = useState("");
  const [periodList, setPeriodList] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [modalEmployee, setModalEmployee] = useState(null);

  const ROWS_PER_PAGE = 10;

  const selectedPeriodName = useMemo(
    () =>
      periodList.find((p) => String(p.id) === String(selectedPeriodId))?.name ||
      "",
    [periodList, selectedPeriodId]
  );

  // Period name → { year, month } for the attendance-summary fetch
  const periodMeta = useMemo(
    () => parsePeriodName(selectedPeriodName),
    [selectedPeriodName]
  );

  useEffect(() => {
    dispatch(getGradeList());
  }, [dispatch]);

  // Fetch dataset on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const periods = await fetchPeriodList();
      if (!cancelled) {
        setPeriodList(periods);
        dispatch(fetchPayrollEntityList("payrollComponents"));
        dispatch(fetchSalaryComponentSummary({ periodId: periods[0]?.id, gradeCode: "" }));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Fetch monthly attendance summary whenever period changes
  useEffect(() => {
    if (periodMeta?.year && periodMeta?.month) {
      dispatch(
        fetchMonthlyAttendanceSummary({
          year: periodMeta.year,
          month: periodMeta.month,
        })
      );
    }
  }, [dispatch, periodMeta]);

  const handleRefresh = async () => {
    const periods = await fetchPeriodList();

    setPeriodList(periods);
    setModalEmployee(null);
    dispatch(clearPayrollBreakdown());

    const currentPeriodExists = periods.some(
      (p) => String(p.id) === String(selectedPeriodId)
    );

    const periodId = currentPeriodExists ? selectedPeriodId : periods[0]?.id;

    if (periodId) {
      setSelectedPeriodId(periodId);
      dispatch(fetchSalaryComponentSummary({ periodId, gradeCode: selectedGrade }));
    }
  };

  useEffect(() => {
    if (!selectedPeriodId && periodList.length > 0) {
      setSelectedPeriodId(periodList[0].id);
    }
  }, [periodList, selectedPeriodId]);

  // Rows for selected period, deduped per employee
  const periodEmployees = useMemo(() => {
    if (!employees || !Array.isArray(employees) || !selectedPeriodName)
      return [];
    const rows = employees.filter(
      (emp) => String(emp.HPH_PAYROLL_PERIOD_ID) === String(selectedPeriodId)
    );
    const seenEmp = new Set();
    return rows.filter((row) => {
      const key = row.HED_SERVICE_NO || row.HED_EMPLOYEE_ID;
      if (seenEmp.has(key)) return false;
      seenEmp.add(key);
      return true;
    });
  }, [employees, selectedPeriodId]);

  const handlePeriodChange = (newPeriodId) => {
    setSelectedPeriodId(newPeriodId);
    setSearch("");
    setModalEmployee(null);

    dispatch(clearPayrollBreakdown());

    if (newPeriodId) {
      dispatch(fetchSalaryComponentSummary({ periodId: newPeriodId, gradeCode: selectedGrade }));
    }
  };

  const handleGradeChange = (newGradeCode) => {
    setSelectedGrade(newGradeCode);
    setSearch("");
    setModalEmployee(null);
    dispatch(clearPayrollBreakdown());

    if (selectedPeriodId) {
      dispatch(fetchSalaryComponentSummary({ periodId: selectedPeriodId, gradeCode: newGradeCode }));
    }
  };

  const filteredEmployees = useMemo(() => {
    if (!periodEmployees || !Array.isArray(periodEmployees)) return [];
    const q = search.trim().toLowerCase();
    if (!q) return periodEmployees;
    return periodEmployees.filter((employee) => {
      const visible = VISIBLE_COLUMNS.map((k) =>
        String(employee[k] ?? "").toLowerCase()
      );
      return visible.some((v) => v.includes(q));
    });
  }, [periodEmployees, search]);

  const loadBreakdown = useCallback(
    (serviceNo, force = false) => {
      if (!selectedPeriodId || !serviceNo) return;
      if (!force) {
        if (breakdowns[serviceNo]) return;
        if (breakdownLoading[serviceNo]) return;
      }
      dispatch(
        fetchPayrollBreakdown({ periodId: selectedPeriodId, serviceNo })
      );
    },
    [dispatch, selectedPeriodId, breakdowns, breakdownLoading]
  );

  const openModal = (row) => {
    setModalEmployee(row);
    const serviceNo = row.HED_SERVICE_NO;
    if (!breakdowns[serviceNo]) {
      loadBreakdown(serviceNo);
    }
  };

  const closeModal = () => setModalEmployee(null);

  /**
   * Look up the pre-computed monthly attendance summary for an employee.
   * Returns zeros if no data is available.
   */
  const getAttendanceSummary = useCallback(
    (emp) => {
      const serviceNo = String(emp?.HED_SERVICE_NO || "").trim();
      const s = monthlySummary[serviceNo];
      if (s) {
        return {
          workingDays: s.workingDays ?? 0,
          offDays: s.offDays ?? 0,
          attendanceDays:
            s.attendanceDays ??
            (Number(s.workingDays || 0) + Number(s.offDays || 0)),
          holidaysWorked: s.holidaysWorked ?? 0,
          poyaDaysWorked: s.poyaDaysWorked ?? 0,
        };
      }
      return {
        workingDays: 0,
        offDays: 0,
        attendanceDays: 0,
        holidaysWorked: 0,
        poyaDaysWorked: 0,
      };
    },
    [monthlySummary]
  );

  const employeeColumns = useMemo(() => {
    const cols = VISIBLE_COLUMNS.map((key) => {
      const isCurrency = CURRENCY_FIELDS.has(key);
      const isStatus = key === "HED_IS_ACTIVE";
      return {
        key,
        label: FIELD_LABELS[key] || key,
        className: isCurrency
          ? "font-semibold text-green-600 text-right"
          : "text-gray-600",
        align: isCurrency ? "right" : "left",
        render: (row) => {
          const value = row[key];
          if (isStatus) {
            const status = formatStatus(value);
            const colorClass = getStatusColor(status);
            return (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
              >
                {status}
              </span>
            );
          }
          return formatCellValue(key, value);
        },
      };
    });

    cols.push({
      key: "__view",
      label: "Action",
      className: "text-center w-20",
      align: "center",
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openModal(row);
          }}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
          title="View salary breakdown"
        >
          <Eye size={14} />
          View
        </button>
      ),
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdowns, selectedPeriodId]);

  /* ──────────────────────────────────────────────────────────────────
     Export V1: Salary Summary
     ────────────────────────────────────────────────────────────────── */
  const exportToExcelV1 = async () => {
    if (!filteredEmployees || filteredEmployees.length === 0) {
      alert("No data to export");
      return;
    }

    const breakdownCache = {};

    for (const emp of filteredEmployees) {
      const serviceNo = emp.HED_SERVICE_NO;
      const cached = breakdowns[serviceNo];

      if (cached) {
        breakdownCache[serviceNo] = cached;
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        const payload = await dispatch(
          fetchPayrollBreakdown({
            periodId: selectedPeriodId,
            serviceNo,
          })
        ).unwrap();

        breakdownCache[serviceNo] = payload;
      } catch {
        breakdownCache[serviceNo] = null;
      }
    }

    const earningComponents = allComponents.filter(
      (c) => c.type === "Earning" || c.type === "E"
    );

    const deductionComponents = allComponents.filter(
      (c) =>
        (c.type === "Deduction" || c.type === "D") &&
        c.code !== "EPF-EE"
    );

    const getComponentAmount = (bd, code) => {
      if (!bd) return "-";

      const match =
        (bd.earnings || []).find(
          (r) => (r.componentCode || r.componentName) === code
        ) ||
        (bd.deductions || []).find(
          (r) => (r.componentCode || r.componentName) === code
        );

      return match ? Number(match.amount) : "-";
    };

    const workbook = new ExcelJS.Workbook();

    const worksheet = workbook.addWorksheet("Salary Summary");

    const now = new Date();

    const dateStr = now.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const timeStr = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const monthDisplay =
      selectedPeriodName ||
      `${now.toLocaleString("en-US", {
        month: "short",
      })}-${now.getFullYear()}`;

    const baseColumns = VISIBLE_COLUMNS.filter(
      (key) => key !== "HPH_NET_SALARY"
    );

    const bankHeaders = [
      "Bank Name",
      "Bank Account Number",
      "Bank Code",
      "Branch Number",
      "EPF Number",
    ];

    // ── Time headers: now includes Off Days ─────────────────────────
    const timeHeaders = [
      "Company Working Days",
      "Attendance Days",
      "Working Days (Week)",
      "Off Days",
      // "Present Days",
      "No Pay Days",
      "OT Hours",
    ];

    const earningHeaders = earningComponents.map((c) => c.name);

    const deductionHeaders = deductionComponents.map((c) => c.name);

    const headers = [
      ...baseColumns.map((k) => FIELD_LABELS[k] || k),
      ...bankHeaders,
      ...timeHeaders,
      ...earningHeaders,
      "Total Earnings to Gross",
      ...deductionHeaders,
      "Total Deductions",
      "Gross Pay",
      "EPF Contribution 8%",
      "Employee Contributions & Taxes",
      "Net Pay",
      "EPF Contribution 12%",
      "ETF Contribution 3%",
      "Employer Contributions & Taxes",
    ];

    const totalColumnCount = headers.length;

    // Report Title
    const headingText = `Salary Component Report Month ${monthDisplay} | Time ${dateStr} ${timeStr}`;

    const headingRow = worksheet.addRow([headingText]);

    worksheet.mergeCells(
      `A1:${excelColumnLetter(totalColumnCount)}1`
    );

    headingRow.getCell(1).alignment = {
      horizontal: "center",
      vertical: "center",
    };

    headingRow.getCell(1).font = {
      bold: true,
      size: 14,
    };

    headingRow.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: "FFE8F0E8",
      },
    };

    headingRow.height = 30;

    // Header row
    const headerRow = worksheet.addRow(headers);

    headerRow.height = 32;

    headerRow.eachCell((cell) => {
      cell.alignment = {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      };

      cell.font = {
        bold: true,
        size: 11,
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "FFE8F0E8",
        },
      };

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Data rows
    filteredEmployees.forEach((emp) => {
      const bd = breakdownCache[emp.HED_SERVICE_NO];
      const header = bd?.header || {};
      const summary = getAttendanceSummary(emp);

      /* Base employee information */
      const baseValues = baseColumns.map((key) => {
        if (DATE_FIELDS.has(key)) {
          return toDateCell(emp[key]);
        }

        if (key === "HED_GENDER") {
          return genderLabel(emp[key]);
        }

        if (key === "HED_IS_ACTIVE") {
          return formatStatus(emp[key]);
        }

        return emp[key] ?? "";
      });

      const bankValues = [
        emp.HBD_BANK_NAME || "-",
        emp.HBD_ACCOUNT_NO || "-",
        emp.HBD_BRANCH_CODE || "-",
        emp.HBD_BRANCH_NO || "-",
        emp.HED_EPF_NO || "-",
      ];

      const timeValues = [
        companyWorkingDays || "-",
        summary.attendanceDays ?? "-",   // → "Attendance Days"
        summary.workingDays ?? "-",      // → "Working Days"
        summary.offDays ?? "-",          // → "Off Days"
        // header.presentDays ?? "-",       // → "Present Days"
        header.noPayDays ?? "-",         // → "No Pay Days"
        header.otHours ?? "-",           // → "OT Hours"
      ];

      // Earnings
      let totalEarningsToGross = 0;

      const earningValues = earningComponents.map((c) => {
        const amount = getComponentAmount(bd, c.code);

        if (amount !== "-") {
          totalEarningsToGross += Number(amount);
        }

        return amount;
      });

      // Deductions
      let totalDeductions = 0;

      const deductionValues = deductionComponents.map((c) => {
        const amount = getComponentAmount(bd, c.code);

        if (amount !== "-") {
          totalDeductions += Number(amount);
        }

        return amount;
      });

      // EPF
      const epf8Raw = getComponentAmount(bd, "EPF-EE");

      const epf8 = epf8Raw !== "-" ? epf8Raw : Number(header.employeeEpf || 0);

      const grossPay = totalEarningsToGross - totalDeductions;

      const employeeTaxes = epf8;

      const netPay = header.netSalary ?? "-";

      const epf12 = Number(header.employeeEpf || 0);

      const etf3 = Number(header.employerEtf || 0);

      const employerTaxes = epf12 + etf3;

      const dataRow = worksheet.addRow([
        ...baseValues,
        ...bankValues,
        ...timeValues,
        ...earningValues,
        totalEarningsToGross || "-",
        ...deductionValues,
        totalDeductions || "-",
        grossPay || "-",
        epf8 || "-",
        employeeTaxes || "-",
        netPay,
        epf12 || "-",
        etf3 || "-",
        employerTaxes || "-",
      ]);

      dataRow.eachCell(
        { includeEmpty: true },
        (cell, colNumber) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };

          const isBaseColumn = colNumber <= baseColumns.length;
          const key = isBaseColumn ? baseColumns[colNumber - 1] : null;

          const isBankColumn = colNumber > baseColumns.length && colNumber <= baseColumns.length + bankHeaders.length;
          const isBankNameColumn = colNumber === baseColumns.length + 1;

          const isTimeColumn = colNumber > baseColumns.length + bankHeaders.length && colNumber <= baseColumns.length + bankHeaders.length + timeHeaders.length;

          const isFinancialColumn = colNumber > baseColumns.length + bankHeaders.length + timeHeaders.length;

          const leftAlignedFields = new Set([
            "HED_REPORT_NAME",
            "HLD_LOCATION_NAME",
            "HED_DESIGNATION_NAME",
            "HDD_DEPARTMENT_NAME",
            "HED_GROUP",
            "HED_COSTCENTER",
          ]);

          // Dates
          if (
            isBaseColumn &&
            DATE_FIELDS.has(key) &&
            cell.value instanceof Date
          ) {
            cell.numFmt = key === "HED_DATE_OF_BIRTH" ? "dd-mm-yyyy" : "dd-mmm-yyyy";
            cell.alignment = {
              horizontal: "center",
              vertical: "center",
            };
          }

          // Financial values
          else if (isFinancialColumn) {
            cell.numFmt = "#,##0.00";
            cell.alignment = {
              horizontal: "right",
              vertical: "center",
            };
          }

          // Bank name + text fields
          else if ((isBaseColumn && leftAlignedFields.has(key)) || isBankNameColumn) {
            cell.alignment = {
              horizontal: "left",
              vertical: "center",
            };
          }

          // Time / bank / general value
          else if (isBankColumn || isTimeColumn) {
            cell.alignment = {
              horizontal: "center",
              vertical: "center",
            };
          }

          else {
            cell.alignment = {
              horizontal: "center",
              vertical: "center",
            };
          }
        }
      );
    });

    worksheet.columns = [
      ...baseColumns.map((key) => {
        if (key === "HED_SERVICE_NO") {
          return { width: 14 };
        }

        if (key === "HED_REPORT_NAME") {
          return { width: 30 };
        }

        if (key === "HLD_LOCATION_NAME") {
          return { width: 30 };
        }

        if (DATE_FIELDS.has(key)) {
          return { width: 16 };
        }

        if (key === "HED_DESIGNATION_NAME") {
          return { width: 24 };
        }

        if (key === "HEC_CATEGORY_NAME") {
          return { width: 22 };
        }

        if (key === "HDD_DEPARTMENT_NAME") {
          return { width: 22 };
        }

        if (key === "HED_GROUP") {
          return { width: 20 };
        }

        if (key === "HED_COSTCENTER") {
          return { width: 18 };
        }

        return { width: 16 };
      }),

      ...bankHeaders.map((header) => {
        if (header === "Bank Name") {
          return { width: 24 };
        }

        if (header === "Bank Account Number") {
          return { width: 22 };
        }

        return { width: 18 };
      }),

      ...timeHeaders.map(() => ({
        width: 15,
      })),

      ...earningHeaders.map(() => ({
        width: 18,
      })),

      { width: 22, },

      ...deductionHeaders.map(() => ({
        width: 18,
      })),

      { width: 20, },
      { width: 20, },
      { width: 22, },
      { width: 28, },
      { width: 20, },
      { width: 20, },
      { width: 28, },
    ];

    // Freeze panes
    worksheet.views = [
      {
        state: "frozen",
        xSplit: 2,
        ySplit: 3,
      }
    ];

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });

    const gradeName = selectedGrade
      ? grades.find(g => g.HGD_GRADE_CODE === selectedGrade)?.HGD_GRADE_NAME || selectedGrade
      : 'All Grades';
    const safeFileNameGrade = gradeName.replace(/[^a-z0-9]/gi, '_');

    saveAs(blob, `Salary_Summary_${monthDisplay}_${safeFileNameGrade}_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  /*
  |--------------------------------------------------------------------------
  | Export to Excel — full Original-sheet layout
  |--------------------------------------------------------------------------
  */
  const exportToExcel = async () => {
    if (!filteredEmployees || filteredEmployees.length === 0) {
      alert("No data to export");
      return;
    }

    /* ── 1. Fetch any missing breakdowns (per employee) ─────────────── */
    const breakdownCache = {};
    for (const emp of filteredEmployees) {
      const serviceNo = emp.HED_SERVICE_NO;
      const cached = breakdowns[serviceNo];
      if (cached) {
        breakdownCache[serviceNo] = cached;
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const payload = await dispatch(
          fetchPayrollBreakdown({ periodId: selectedPeriodId, serviceNo })
        ).unwrap();
        breakdownCache[serviceNo] = payload;
      } catch {
        breakdownCache[serviceNo] = null;
      }
    }

    /* ── 2. Component lookup by alias ───────────────────────────────── */
    const lookupComponent = (bd, aliasKey) => {
      if (!bd) return null;
      const map = Object.fromEntries(
        [...(bd.earnings || []), ...(bd.deductions || []), ...(bd.contributions || [])]
          .map((r) => [r.componentName || r.componentCode || "", r.amount])
      );
      return findComponentAmount(map, aliasKey);
    };

    /* ── 3. Derived totals ──────────────────────────────────────────── */
    const grossPay = (bd) => {
      const h = bd?.header;
      if (!h) return null;
      return (
        Number(h.totalEarnings || 0) -
        (Number(h.totalDeductions || 0) - Number(h.employeeEpf || 0))
      );
    };
    const empTaxes = (bd) => {
      const h = bd?.header;
      if (!h) return null;
      return Number(h.employeeEpf || 0) + Number(h.paye || 0);
    };
    const employerTaxes = (bd) => {
      const h = bd?.header;
      if (!h) return null;
      return Number(h.employerEpf || 0) + Number(h.employerEtf || 0);
    };

    /* ── 4. Column layout ──────────────── */
    const COLUMNS = [
      // 1–15: Identity / Master
      { header: "Serial Number", get: (_, __, i) => i + 1, fmtType: "int" },
      { header: "Month", get: (e) => e.HPP_PERIOD_NAME, fmtType: "text" },
      { header: "Location", get: (e) => e.HLD_LOCATION_NAME, fmtType: "text", align: "left" },
      { header: "Staff Id", get: (e) => e.HED_SERVICE_NO, fmtType: "text" },
      { header: "Name", get: (e) => e.HED_REPORT_NAME, fmtType: "text", align: "left" },
      { header: "Date of Joining", get: (e) => toDateCell(e.HED_DATE_OF_JOINING), fmtType: "date" },
      { header: "Date of Leaving", get: (e) => toDateCell(e.HED_LEAVING_DATE), fmtType: "date" },
      { header: "Date of Birth", get: (e) => toDateCell(e.HED_DATE_OF_BIRTH), fmtType: "date" },
      { header: "Gender", get: (e) => genderLabel(e.HED_GENDER), fmtType: "text" },
      { header: "Designation", get: (e) => e.HED_DESIGNATION_NAME, fmtType: "text", align: "left" },
      { header: "Category", get: (e) => e.HEC_CATEGORY_NAME, fmtType: "text" },
      { header: "Status", get: (e) => formatStatus(e.HED_IS_ACTIVE), fmtType: "text" },
      { header: "Group", get: (e) => e.HED_GROUP, fmtType: "text", align: "left" },
      { header: "Company", get: (e) => companyFromLocation(e.HLD_LOCATION_NAME), fmtType: "text", align: "left" },
      { header: "SBU", get: (e) => e.HED_SBU || e.HEC_SBU || "-", fmtType: "text" },
      { header: "Department", get: (e) => e.HDD_DEPARTMENT_NAME, fmtType: "text", align: "left" },
      { header: "Cost Center", get: (e) => e.HED_COSTCENTER, fmtType: "text", align: "left" },
      { header: "Province", get: (e) => e.HED_PROVINCE || "-", fmtType: "text" },
      { header: "District", get: (e) => e.HED_DISTRICT || "-", fmtType: "text" },
      { header: "Polling Division", get: (e) => e.HED_POLLING_DIVISION || "-", fmtType: "text" },

      // 21–26: Bank / NIC / EPF
      { header: "Bank Name", get: (e) => e.HBD_BANK_NAME || "-", fmtType: "text", align: "left" },
      { header: "Bank Account Number", get: (e) => e.HEB_ACCOUNT_NUMBER || "-", fmtType: "text" },
      { header: "Bank Code", get: (e) => e.HBD_BANK_CODE || e.HBD_BRANCH_CODE || "-", fmtType: "text" },
      { header: "Branch Name", get: (e) => e.HBB_BRANCH_NAME || e.HBD_BRANCH_NO || "-", fmtType: "text", align: "left" },
      { header: "NIC", get: (e) => e.HED_NIC_NO, fmtType: "text" },
      { header: "EPF", get: (e) => e.HED_EPF_NO || "-", fmtType: "text" },

      // 27–37: Time & Attendance
      { header: "Company Working Days", get: () => companyWorkingDays, fmtType: "num" },
      { header: "Attendance Days", get: (e) => getAttendanceSummary(e).attendanceDays, fmtType: "num" },
      { header: "Working Days", get: (e) => getAttendanceSummary(e).workingDays, fmtType: "num" },
      { header: "Off Days", get: (e) => getAttendanceSummary(e).offDays, fmtType: "num" },
      { header: "Pay Days", get: (_, bd) => bd?.header?.presentDays ?? null, fmtType: "num" },
      { header: "No Pay Days", get: (_, bd) => bd?.header?.noPayDays ?? null, fmtType: "num" },
      { header: "Full Day Worked", get: () => "-", fmtType: "text" },
      { header: "Days Worked By Leave", get: () => "-", fmtType: "text" },
      { header: "Weekoffs Worked", get: () => "-", fmtType: "text" },
      { header: "Holidays Worked", get: (e) => getAttendanceSummary(e).holidaysWorked, fmtType: "num" },
      { header: "Poya Days Worked", get: (e) => getAttendanceSummary(e).poyaDaysWorked, fmtType: "num" },
      { header: "Salary Status", get: () => "-", fmtType: "text" },
      { header: "Fn F", get: () => "-", fmtType: "text" },

      // 38–57: Earnings components
      { header: "Basic / Prorated Basic", get: (_, bd) => lookupComponent(bd, "basic"), fmtType: "money" },
      { header: "BRA 2005", get: (_, bd) => lookupComponent(bd, "bra2005"), fmtType: "money" },
      { header: "BRA 2016 Stage 01", get: (_, bd) => lookupComponent(bd, "bra2016Stage01"), fmtType: "money" },
      { header: "BRA 2016 Stage 02", get: (_, bd) => lookupComponent(bd, "bra2016Stage02"), fmtType: "money" },
      { header: "Daily Basic", get: (_, bd) => lookupComponent(bd, "dailyBasic"), fmtType: "money" },
      { header: "Daily BRA 2005", get: (_, bd) => lookupComponent(bd, "dailyBra2005"), fmtType: "money" },
      { header: "Daily BRA 2016 Stage 01", get: (_, bd) => lookupComponent(bd, "dailyBra2016Stage01"), fmtType: "money" },
      { header: "Daily BRA 2016 Stage 02", get: (_, bd) => lookupComponent(bd, "dailyBra2016Stage02"), fmtType: "money" },
      { header: "Total for EPF & ETF", get: (_, bd) => bd?.header?.totalEarnings ?? null, fmtType: "money" },
      { header: "No Pay", get: (_, bd) => lookupComponent(bd, "noPay"), fmtType: "money" },
      { header: "This Month Salary for EPF & ETF", get: (_, bd) => lookupComponent(bd, "thisMonthSalaryForEpfEtf"), fmtType: "money" },
      { header: "Exigencies of Service Allowance", get: (_, bd) => lookupComponent(bd, "exigenciesOfServiceAllowance"), fmtType: "money" },
      { header: "Attendance Allowance", get: (_, bd) => lookupComponent(bd, "attendanceAllowance"), fmtType: "money" },
      { header: "Attendance Incentive", get: (_, bd) => lookupComponent(bd, "attendanceIncentive"), fmtType: "money" },
      { header: "Special Project Allowance", get: (_, bd) => lookupComponent(bd, "specialProjectAllowance"), fmtType: "money" },
      { header: "Training Allowance", get: (_, bd) => lookupComponent(bd, "trainingAllowance"), fmtType: "money" },
      { header: "1.5 OT", get: (_, bd) => lookupComponent(bd, "otPayment"), fmtType: "money" },
      { header: "Travelling Allowance", get: (_, bd) => lookupComponent(bd, "travellingAllowance"), fmtType: "money" },
      { header: "Extra Day Payment", get: (_, bd) => lookupComponent(bd, "extraDayPayment"), fmtType: "money" },
      { header: "Extra Day Payment - 24 Hrs Shift", get: (_, bd) => lookupComponent(bd, "extraDayPayment"), fmtType: "money" },
      { header: "Field Service", get: (_, bd) => lookupComponent(bd, "fieldService"), fmtType: "money" },
      { header: "Meal Allowance", get: (_, bd) => lookupComponent(bd, "mealAllowance"), fmtType: "money" },

      // 58: Total earnings
      { header: "Total Earnings to Gross", get: (_, bd) => bd?.header?.totalEarnings ?? null, fmtType: "money", bold: true },

      // 59–61: Deductions
      { header: "Salary Advance", get: (_, bd) => lookupComponent(bd, "salaryAdvance"), fmtType: "money" },
      { header: "Chummery Deduction", get: (_, bd) => lookupComponent(bd, "chummeryDeduction"), fmtType: "money" },
      { header: "Late Hours Deduction", get: (_, bd) => lookupComponent(bd, "lateHoursDeduction"), fmtType: "money" },

      // 62–71: Totals & statutory
      { header: "Total Deductions from Gross", get: (_, bd) => bd?.header?.totalDeductions ?? null, fmtType: "money", bold: true },
      { header: "Gross Pay", get: (_, bd) => grossPay(bd), fmtType: "money", bold: true },
      { header: "EPF Contribution 8%", get: (_, bd) => bd?.header?.employeeEpf ?? null, fmtType: "money" },
      { header: "APIT", get: (_, bd) => bd?.header?.paye ?? null, fmtType: "money" },
      { header: "Employee Contributions & Taxes", get: (_, bd) => empTaxes(bd), fmtType: "money", bold: true },
      { header: "Net Pay", get: (_, bd) => bd?.header?.netSalary ?? null, fmtType: "money", bold: true },
      { header: "EPF Contribution 12%", get: (_, bd) => bd?.header?.employerEpf ?? null, fmtType: "money" },
      { header: "ETF Contribution 3%", get: (_, bd) => bd?.header?.employerEtf ?? null, fmtType: "money" },
      { header: "Employer Contributions & Taxes", get: (_, bd) => employerTaxes(bd), fmtType: "money", bold: true },

      // 72–74: Base / Meal / OT hours
      { header: "Base Basic", get: (_, bd) => lookupComponent(bd, "baseBasic"), fmtType: "money" },
      { header: "Meal Allowance - Lunch Rate", get: (_, bd) => lookupComponent(bd, "mealAllowance"), fmtType: "money" },
      { header: "Total OT Hours", get: (_, bd) => bd?.header?.otHours ?? null, fmtType: "num" },

      // 75–94: Payroll setup flags (placeholders)
      { header: "* Basic / Prorated Basic", get: () => "-", fmtType: "text" },
      { header: "* Daily Basic", get: () => "-", fmtType: "text" },
      { header: "* Exigencies of Service Allowance", get: () => "-", fmtType: "text" },
      { header: "* Attendance Allowance", get: () => "-", fmtType: "text" },
      { header: "* Special Project Allowance", get: () => "-", fmtType: "text" },
      { header: "* Accommodation Allowance", get: () => "-", fmtType: "text" },
      { header: "* Field Service", get: () => "-", fmtType: "text" },
      { header: "* Ignore Attendance for Payroll?", get: () => "-", fmtType: "text" },
      { header: "* Meal Allowance - Lunch Rate", get: () => "-", fmtType: "text" },
      { header: "* Machine Day Rate", get: () => "-", fmtType: "text" },
      { header: "* Pay Attendance Incentive on Slab Basis", get: () => "-", fmtType: "text" },
      { header: "* Extra Day Payment Rate", get: () => "-", fmtType: "text" },
      { header: "* Agreed OT Rate Per Hour", get: () => "-", fmtType: "text" },
      { header: "* Working Days for No Pay", get: () => "-", fmtType: "text" },
      { header: "* Pay for Balance Lieu Leave?", get: () => "-", fmtType: "text" },
      { header: "* not Entitle for OT?", get: () => "-", fmtType: "text" },
      { header: "* not Entitle for Pre-OT?", get: () => "-", fmtType: "text" },
      { header: "* Do not Calculate EPF & ETF?", get: () => "-", fmtType: "text" },
      { header: "* Week Off / Holiday - No OT?", get: () => "-", fmtType: "text" },
      { header: "* Week Off / Holiday - After Shift OT?", get: () => "-", fmtType: "text" },
      { header: "* Pay Half of Daily Rate for Saturdays?", get: () => "-", fmtType: "text" },
      { header: "* Security Staff with 24 Hrs Shift?", get: () => "-", fmtType: "text" },
    ];

    /* ── 5. Build workbook ──────────────────────────────────────────── */
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Salary Summary");

    const now = new Date();
    const dateStr = now.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
    const timeStr = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    const monthDisplay =
      selectedPeriodName ||
      `${now.toLocaleString("en-US", { month: "short" })}-${now.getFullYear()}`;

    const totalCols = COLUMNS.length;
    const heading = worksheet.addRow([
      `Salary Component Report Month ${monthDisplay} | Time ${dateStr} ${timeStr}`,
    ]);
    worksheet.mergeCells(`A1:${excelColumnLetter(totalCols)}1`);
    heading.getCell(1).alignment = { horizontal: "center", vertical: "center" };
    heading.getCell(1).font = { bold: true, size: 14 };
    heading.height = 30;

    worksheet.addRow([]);

    const headerRow = worksheet.addRow(COLUMNS.map((c) => c.header));
    headerRow.height = 32;
    headerRow.eachCell((cell) => {
      cell.alignment = { horizontal: "center", vertical: "center", wrapText: true };
      cell.font = { bold: true, size: 11 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8F0E8" } };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    /* ── 6. Data rows ───────────────────────────────────────────────── */
    filteredEmployees.forEach((emp, idx) => {
      const bd = breakdownCache[emp.HED_SERVICE_NO];

      const values = COLUMNS.map((col) => {
        const raw = col.get(emp, bd, idx);
        if (raw === null || raw === undefined || raw === "") return "-";
        if (col.fmtType === "money" || col.fmtType === "num" || col.fmtType === "int") {
          const n = Number(raw);
          return Number.isNaN(n) ? raw : n;
        }
        return raw;
      });

      const row = worksheet.addRow(values);

      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const col = COLUMNS[colNumber - 1];
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (col.fmtType === "money") {
          cell.numFmt = "#,##0.00";
          cell.alignment = { horizontal: "right", vertical: "center" };
          if (col.bold) cell.font = { bold: true };
        } else if (col.fmtType === "num") {
          cell.numFmt = "#,##0.00";
          cell.alignment = { horizontal: "right", vertical: "center" };
        } else if (col.fmtType === "int") {
          cell.alignment = { horizontal: "center", vertical: "center" };
        } else if (col.fmtType === "date") {
          if (cell.value instanceof Date) cell.numFmt = "dd-mmm-yyyy";
          cell.alignment = { horizontal: "center", vertical: "center" };
        } else if (col.align === "left") {
          cell.alignment = { horizontal: "left", vertical: "center" };
        } else {
          cell.alignment = { horizontal: "center", vertical: "center" };
        }
      });
    });

    /* ── 7. Column widths ──────────────────────────────────────────── */
    worksheet.columns = COLUMNS.map((c) => {
      if (c.header === "Name") return { width: 30 };
      if (c.header === "Location") return { width: 30 };
      if (c.header === "Designation") return { width: 26 };
      if (c.header === "Department" || c.header === "Group") return { width: 22 };
      if (c.header === "Bank Account Number") return { width: 22 };
      if (c.header === "Working Days" || c.header === "Off Days") return { width: 14 };
      if (c.header.includes("Allowance") || c.header.includes("Payment")) return { width: 22 };
      if (c.header.startsWith("*")) return { width: 26 };
      if (c.fmtType === "money") return { width: 16 };
      if (c.fmtType === "date") return { width: 14 };
      return { width: 14 };
    });

    worksheet.views = [{ state: "frozen", xSplit: 5, ySplit: 3 }];

    /* ── 8. Save ───────────────────────────────────────────────────── */
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const gradeName = selectedGrade
      ? grades.find(g => g.HGD_GRADE_CODE === selectedGrade)?.HGD_GRADE_NAME || selectedGrade
      : 'All Grades';
    const safeFileNameGrade = gradeName.replace(/[^a-z0-9]/gi, '_');

    saveAs(
      blob,
      `Salary_Component_${monthDisplay}_${safeFileNameGrade}_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // ─── Error state ──────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <h3 className="font-semibold">Error loading data</h3>
          <p>{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* ── Header ── */}
      <Breadcrumb
        trail={["Payroll", "Salary Details Report"]}
        title="Salary Details Report"
        subtitle={`View employee Salary Details Report for ${selectedPeriodName || "selected period"
          }. Click "View" on a row to see how Net Salary is calculated.`}
        action={
          <div className="flex gap-2">
            <button
              onClick={exportToExcelV1}
              className={`${BREADCRUMB_ACTION_BTN} bg-green-600 hover:bg-green-700 text-white`}
              disabled={!filteredEmployees || filteredEmployees.length === 0}
              title="Export salary summary report"
            >
              <Download size={15} />
              Salary Summary
            </button>

            <button
              onClick={exportToExcel}
              className={`${BREADCRUMB_ACTION_BTN} bg-green-600 hover:bg-green-700 text-white`}
              disabled={!filteredEmployees || filteredEmployees.length === 0}
              title="Export the full salary component report"
            >
              <Download size={15} />
              Full Report
            </button>

            <button onClick={handleRefresh} className={BREADCRUMB_ACTION_BTN}>
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        }
      />

      {/* ── Filters ── */}
      <div className="flex items-end gap-4">
        <div>
          <span className={LABEL}>Payroll Period</span>
          <select
            value={selectedPeriodId}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className={`${FIELD} w-64`}
          >
            {periodList.length === 0 && (
              <option value="">No periods available</option>
            )}
            {periodList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || `Period ${p.id}`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className={LABEL}>Grade</span>
          <select
            value={selectedGrade}
            onChange={(e) => handleGradeChange(e.target.value)}
            className={`${FIELD} w-48`}
          >
            <option value="">All</option>
            {grades.map((grade) => (
              <option key={grade.HGD_GRADE_CODE} value={grade.HGD_GRADE_CODE}>
                {grade.HGD_GRADE_NAME}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className={LABEL}>Search</span>
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search any visible field..."
              className={`${FIELD} pl-9 w-72`}
            />
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <Table
        title="Employee Details"
        meta={`${filteredEmployees.length} of ${employees?.length || 0} employee${employees?.length === 1 ? "" : "s"
          }${selectedPeriodName ? ` for ${selectedPeriodName}` : ""}`}
        columns={employeeColumns}
        rows={filteredEmployees}
        rowKey={(row) => row.HED_SERVICE_NO || row.HED_EMPLOYEE_ID}
        loading={loading}
        loadingMessage="Loading employee details..."
        emptyMessage={
          !employees || employees.length === 0
            ? "No employee data available. Select a payroll period to view data."
            : "No results match your search criteria."
        }
        minWidth={1700}
        pageSize={ROWS_PER_PAGE}
        scrollClassName=""
        emptyValue="-"
        defaultSort={{ key: "HED_SERVICE_NO", dir: "asc" }}
      />

      {/* ── Modal ── */}
      <SalaryBreakdownModal
        open={!!modalEmployee}
        onClose={closeModal}
        employee={modalEmployee}
        state={
          modalEmployee
            ? {
              loading: !!breakdownLoading[modalEmployee.HED_SERVICE_NO],
              error: breakdownErrors[modalEmployee.HED_SERVICE_NO] || null,
              data: breakdowns[modalEmployee.HED_SERVICE_NO] || null,
            }
            : null
        }
        onRetry={() =>
          modalEmployee && loadBreakdown(modalEmployee.HED_SERVICE_NO, true)
        }
      />
    </div>
  );
}

export default SalarySummary;